import { useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuidv4 } from 'uuid'
import { gateCompletionUserIds, isGateCleared } from '../../logic/gateUtils'
import GateSubmission, { type GateMediaType } from '../../model/GateSubmission'
import type Gate from '../../model/Gate'
import { dataSource, storageService } from '../../service/domain/_service.config'
import { getInitials } from '../../ui/primitives/Avatar'
import { useChallengeContext } from '../challenge/useChallengeContext'
import { useHasEngagement } from '../engagement/useUserEngagements'
import { useChallengeAccess } from '../membership/useChallengeAccess'
import { useTeam } from '../team/useTeam'
import { useUser } from '../user/useUser'
import { submissionTimeLabel } from './gateUtils'
import {
  GATE_SUBMISSIONS_QUERY_KEY,
  TEAM_GATE_SUBMISSIONS_QUERY_KEY,
  useGate,
  useGateSubmissions,
} from './useGates'

export interface GateMemberStatus {
  userId: string
  initials: string
  photoUrl?: string
  firstName: string
  isYou: boolean
  submitted: boolean
}

export interface GateSubmissionItem {
  id: string
  userId: string
  initials: string
  photoUrl?: string
  name: string
  isYou: boolean
  mediaUrl: string
  mediaType: GateMediaType
  time: string
}

export interface GateScreenData {
  gateId: string
  checkpointLabel: string
  city: string
  rms: number
  challengeTitle: string
  challengeDescription: string
  imageUrl?: string
  videoUrl?: string
  userHasSeenVideo: boolean
  members: GateMemberStatus[]
  submittedCount: number
  teamSize: number
  userHasSubmitted: boolean
  isCleared: boolean
  submissions: GateSubmissionItem[]
  waitingCount: number
}

function buildScreenData(
  gate: Gate,
  rawSubmissions: GateSubmission[],
  teamMembers: NonNullable<ReturnType<typeof useTeam>['data']>,
  currentUserId: string,
  goal: number,
  userHasSeenVideo: boolean,
): GateScreenData {
  const completedUserIds = new Set(gateCompletionUserIds(gate, rawSubmissions))
  const members: GateMemberStatus[] = teamMembers.members.map((member) => ({
    userId: member.id,
    initials: getInitials(member.firstName, member.lastName),
    photoUrl: member.photoUrl,
    firstName: member.firstName,
    isYou: member.id === currentUserId,
    submitted: completedUserIds.has(member.id),
  }))

  const submittedCount = members.filter((m) => m.submitted).length
  const userHasSubmitted = completedUserIds.has(currentUserId)

  const submissions: GateSubmissionItem[] = [...rawSubmissions]
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    .map((submission) => {
      const member = teamMembers.members.find((m) => m.id === submission.userId)
      return {
        id: submission.id,
        userId: submission.userId,
        initials: member
          ? getInitials(member.firstName, member.lastName)
          : '??',
        photoUrl: member?.photoUrl,
        name: member
          ? member.id === currentUserId
            ? 'You'
            : member.firstName
          : 'Teammate',
        isYou: submission.userId === currentUserId,
        mediaUrl: submission.mediaUrl,
        mediaType: submission.mediaType,
        time: submissionTimeLabel(submission.createdAt),
      }
    })

  return {
    gateId: gate.id,
    checkpointLabel:
      gate.checkpoint.threshold >= goal
        ? 'Final gate'
        : `Gate ${gate.checkpoint.sortOrder + 1}`,
    city: gate.displayCity(),
    rms: gate.checkpoint.threshold,
    challengeTitle: gate.displayTitle(),
    challengeDescription: gate.displayDescription(),
    imageUrl: gate.imageUrl,
    videoUrl: gate.videoUrl,
    userHasSeenVideo,
    members,
    submittedCount,
    teamSize: teamMembers.members.length,
    userHasSubmitted,
    isCleared: isGateCleared(gate, completedUserIds, teamMembers.members.length),
    submissions,
    waitingCount: Math.max(teamMembers.members.length - submittedCount, 0),
  }
}

export function useGateScreenData(gateId: string) {
  const { data: user } = useUser()
  const { teamID } = useChallengeAccess()
  const { data: team, isLoading: teamLoading } = useTeam(teamID)
  const { data: challengeContext, isLoading: challengeLoading } = useChallengeContext()
  const { data: gate, isLoading: gateLoading, error: gateError } = useGate(gateId)
  const { data: submissions, isLoading: submissionsLoading } = useGateSubmissions(gateId)
  const userHasSeenVideo = useHasEngagement('gate', gate?.id)

  const isLoading = teamLoading || gateLoading || challengeLoading || submissionsLoading

  if (isLoading || !user || !team || !gate || !challengeContext || !submissions) {
    return {
      data: null,
      isLoading,
      error: gateError ?? (gateLoading || gate ? null : new Error('Gate not found')),
    }
  }

  const data = buildScreenData(
    gate,
    submissions,
    team,
    user.id,
    challengeContext.goal,
    userHasSeenVideo,
  )

  return { data, isLoading: false, error: null }
}

export function useSubmitGateProof(gateId: string) {
  const queryClient = useQueryClient()
  const { data: user } = useUser()
  const { teamID } = useChallengeAccess()
  const { data: team } = useTeam(teamID)

  return useMutation({
    mutationFn: async (file: File) => {
      if (!user || !teamID || !team) throw new Error('Not on a team')

      const mediaType: GateMediaType = file.type.startsWith('video/') ? 'video' : 'photo'
      const submissionId = uuidv4()
      const mediaUrl = await storageService.uploadGateSubmissionMedia(
        file,
        user.id,
        gateId,
        submissionId,
      )

      const submission = new GateSubmission({
        id: submissionId,
        gateId,
        teamId: team.id,
        userId: user.id,
        mediaUrl,
        mediaType,
        createdAt: new Date(),
      })

      return dataSource.saveGateSubmission(submission)
    },
    onSuccess: (saved) => {
      if (!teamID) return
      const remember = (existing: GateSubmission[] | undefined) => {
        const prev = existing ?? []
        return [...prev.filter((s) => !(s.gateId === saved.gateId && s.userId === saved.userId)), saved]
      }
      queryClient.setQueryData(GATE_SUBMISSIONS_QUERY_KEY(teamID, gateId), remember)
      queryClient.setQueryData(TEAM_GATE_SUBMISSIONS_QUERY_KEY(teamID), remember)
    },
  })
}

export function formatGateMeta(rms: number, city: string) {
  return `${rms.toLocaleString()} RMs · ${city}`
}
